import { Reveal } from "@components/ui";
import { type ReactNode } from "react";

/* ─────────────────────────────────────────────────────────
   Inline SVG Icons
   ───────────────────────────────────────────────────────── */

function PhoneIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.9 20.6C8.4 21.5 10.2 22 12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 13.8 2.5 15.5 3.3 17L2 22L6.9 20.6Z" />
      <path d="M9 10a.5.5 0 0 1 1 0v1a3 3 0 0 0 3 3h1a.5.5 0 0 1 0 1" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────
   Types & Sub-components
   ───────────────────────────────────────────────────────── */

interface SupportCardData {
  icon: ReactNode;
  label: string;
  value: string;
  action: string;
  availability: string;
  color: string;
  isOnline: boolean;
}

function SupportCard({ icon, label, value, action, availability, color, isOnline }: SupportCardData) {
  return (
    <div className="group w-full h-full bg-white rounded-[16px] md:rounded-[20px] p-5 md:p-7 flex flex-col items-start gap-5 md:gap-6 border border-transparent hover:border-[#e8e9ed] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300">
      {/* Top: icon + availability dot */}
      <div className="flex items-center justify-between w-full">
        <div
          className="w-11 h-11 md:w-12 md:h-12 rounded-[12px] flex items-center justify-center transition-all duration-300 group-hover:scale-105"
          style={{ backgroundColor: `${color}14`, color }}
        >
          {icon}
        </div>
        <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#f6f6f9] text-[11px] md:text-[12px] font-medium text-[#62636c] font-albert-sans">
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: isOnline ? "#22c55e" : "#3b82f6" }}
          />
          {availability}
        </span>
      </div>

      {/* Label */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[12px] md:text-[13px] font-medium text-[#62636c] tracking-[-0.02em] uppercase font-albert-sans">
          {label}
        </span>
        <span className="text-[18px] md:text-[22px] lg:text-[24px] font-semibold text-[#090d2c] tracking-[-0.04em] font-albert-sans">
          {value}
        </span>
      </div>

      {/* Action link */}
      <div className="mt-auto pt-1">
        <span className="inline-flex items-center gap-1.5 text-[13px] md:text-[15px] font-medium font-albert-sans transition-all duration-200 group-hover:gap-2.5" style={{ color }}>
          {action}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="transition-transform duration-300 group-hover:translate-x-0.5">
            <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Data
   ───────────────────────────────────────────────────────── */

const supportCards: SupportCardData[] = [
  {
    icon: <PhoneIcon />,
    label: "Phone",
    value: "800-GAZ (42747)",
    action: "Call Now",
    availability: "24/7",
    color: "#122d63",
    isOnline: true,
  },
  {
    icon: <WhatsAppIcon />,
    label: "WhatsApp",
    value: "+971 5 XXX XXXX",
    action: "Chat Now",
    availability: "8 AM – 8 PM",
    color: "#25d366",
    isOnline: true,
  },
  {
    icon: <EmailIcon />,
    label: "Email",
    value: "help@gazglobal.net",
    action: "Send Email",
    availability: "24/7",
    color: "#f24d2b",
    isOnline: true,
  },
];

/* ─────────────────────────────────────────────────────────
   Main component
   ───────────────────────────────────────────────────────── */

export function SupportChannelsSection() {
  return (
    <section className="w-full bg-[#f6f6f9] overflow-hidden py-12 md:py-16 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto font-albert-sans">
        {/* Header */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-4 md:gap-5 mb-10 lg:mb-14">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/[0.08] text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
              Support
            </span>
            <h2 className="text-center text-[24px] md:text-[40px] lg:text-[56px] tracking-[-0.04em] font-semibold text-[#122d63] leading-[1.1]">
              Get in Touch
            </h2>
            <p className="text-center max-w-[520px] text-[14px] md:text-base lg:text-[18px] text-[rgba(10,14,46,0.75)] leading-[1.6]">
              Reach our support team through any of these channels. We're here to help.
            </p>
          </div>
        </Reveal>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
          {supportCards.map((card, index) => (
            <Reveal key={index} direction="up" delay={80 + index * 80} className="h-full">
              <SupportCard {...card} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
